import { createContext, ReactNode, useContext, useState } from 'react';

import { ModalSize } from '../utils';

interface ModalContextValue {
  isOpen: boolean;
  content: ReactNode | null;
  heading?: string;
  size: ModalSize;
  className?: string;
  openModal: (content: ReactNode, heading?: string, size?: ModalSize, className?: string) => void;
  closeModal: () => void;
  setContent: (content: ReactNode) => void;
}

const ModalContext = createContext<ModalContextValue | undefined>(undefined);

export const ModalProvider = ({ children }: { children: ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [content, setContent] = useState<ReactNode | null>(null);
  const [heading, setHeading] = useState<string | undefined>(undefined);
  const [size, setSize] = useState<ModalSize>(ModalSize.Medium);
  // ACP-2183: a modal whose content floats something outside its own box — the date
  // picker's calendar — needs to opt out of the clipping the rest rely on.
  const [className, setClassName] = useState<string | undefined>(undefined);

  const openModal = (
    modalContent: ReactNode,
    modalHeading?: string,
    modalSize: ModalSize = ModalSize.Medium,
    modalClassName?: string,
  ) => {
    setContent(modalContent);
    setHeading(modalHeading);
    setSize(modalSize);
    setClassName(modalClassName);
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
    setContent(null);
    setHeading(undefined);
    setSize(ModalSize.Medium);
    setClassName(undefined);
  };

  const updateContent = (newContent: ReactNode) => {
    setContent(newContent);
  };

  const value: ModalContextValue = {
    className,
    closeModal,
    content,
    heading,
    isOpen,
    openModal,
    setContent: updateContent,
    size,
  };

  return <ModalContext.Provider value={value}>{children}</ModalContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useModal = () => {
  const context = useContext(ModalContext);

  if (!context) {
    throw new Error('useModal must be used within ModalProvider');
  }

  return context;
};
